import {Injectable} from '@angular/core';
import {MedicalTestService} from './medical-test.service';
import {IdName, LoaderService} from '@core/src/public-api';
import {TranslateService} from '@ngx-translate/core';
import {mtestOptionCases, mtestOptionPermission, mtestOptionsActions, mtestOptionValues} from '@shared/constants/mtest.constants';
import {PermissionService} from './permission.service';
import {MedicalTestOptionItem, MedicalTestViewerData} from '@shared/models/medical-test.model';
import {historyOptionCases, historyOptionPermission, historyOptionsActions, historyOptionValues} from '@shared/constants/history.constants';
import {HistoryService} from './history.service';
import {HistoryOptionItem, HistoryViewerData} from '@shared/models/history.model';

@Injectable({
  providedIn: 'root'
})
export class DocumentOptionActionService {

  constructor(
    private medicalTestService: MedicalTestService,
    private historyService: HistoryService,
    private loaderService: LoaderService,
    private permissionService: PermissionService,
    private translateService: TranslateService
  ) { }

  async onSelectOptionMtest(requestId: number, item: MedicalTestOptionItem): Promise<void> {
    this.loaderService.start();
    try {
      switch (item.type) {
        case mtestOptionCases.REFERRAL:
          await this.medicalTestService.getDownloadDocumentReferral(requestId);
          break;
        case mtestOptionCases.RESULTS:
          await this.medicalTestService.getDownloadDocumentResults(requestId);
          break;
        case mtestOptionCases.RESULTS_VIEWER:
          await this.medicalTestService.getDownloadDocumentResultsViewer(requestId);
          break;
        case mtestOptionCases.RESULTS_VIEWER_URL:
          await this.medicalTestService.getOpenViewerResultsUrl(requestId);
          break;
        case mtestOptionCases.IMAGE:
          await this.medicalTestService.getOpenViewerDataImage(requestId, item.code);
          break;
        case mtestOptionCases.IC:
          await this.medicalTestService.getDownloadInformedConsent(requestId, item.id);
        default:
      }
    } finally {
      this.loaderService.stop();
    }
  }

  async onSelectOptionHistory(docId: string, item: HistoryOptionItem, alfrescoGuid?: string): Promise<void> {
    this.loaderService.start();
    try {
      switch (item.type) {
        case historyOptionCases.DOCUMENT:
          await this.historyService.getDownloadDocument(alfrescoGuid);
          break;
        case historyOptionCases.RESULTS_VIEWER:
          await this.historyService.getDownloadDocumentResultsViewer(docId);
          break;
        case historyOptionCases.RESULTS_VIEWER_URL:
          await this.historyService.getOpenViewerResultsUrl(docId);
          break;
        case historyOptionCases.IMAGE:
          await this.historyService.getOpenViewerDataImage(docId, item.code);
          break;
        default:
      }
    } finally {
      this.loaderService.stop();
    }
  }

  async getShareMtestResults(requestId: number, item: MedicalTestOptionItem): Promise<MedicalTestViewerData> {
    this.loaderService.start();
    try {
      switch (item.type) {
        case mtestOptionCases.RESULTS_VIEWER:
          return await this.medicalTestService.getShareDocumentResultsViewer(requestId);
        case mtestOptionCases.RESULTS_VIEWER_URL:
          return await this.medicalTestService.getShareDocumentResultsViewerUrl(requestId);
        case mtestOptionCases.IMAGE:
          return await this.medicalTestService.getShareImageResults(requestId, item.code);
        case mtestOptionCases.RESULTS:
          return await this.medicalTestService.getShareDocumentResults(requestId);
        default:
      }
    } catch (e) {
      throw e;
    } finally {
      this.loaderService.stop();
    }
  }

  async getShareHistoryResults(docId: string, item: HistoryOptionItem, alfrescoGuid?: string): Promise<HistoryViewerData> {
    this.loaderService.start();
    try {
      switch (item.type) {
        case historyOptionCases.RESULTS_VIEWER:
          return await this.historyService.getShareDocumentResultsViewer(docId);
        case historyOptionCases.RESULTS_VIEWER_URL:
          return await this.historyService.getShareDocumentResultsViewerUrl(docId);
        case historyOptionCases.IMAGE:
          return await this.historyService.getShareImageResults(docId, item.code);
        case historyOptionCases.DOCUMENT:
          return await this.historyService.getShareDocumentResults(alfrescoGuid);
        default:
      }
    } catch (e) {
      throw e;
    } finally {
      this.loaderService.stop();
    }
  }

  initOptionValuesMtest(
    optionsToAdd: string[],
    actions: string[],
    images?: IdName[],
    informedConsents?: IdName[]
  ): IdName[] {
    let optionValues = [];
    if (
      optionsToAdd.includes(mtestOptionsActions.REFERRAL)
      && this.permissionService.hasAction(mtestOptionsActions.REFERRAL, actions)
      && this.permissionService.hasPermission(mtestOptionPermission.REFERRAL)
    ) {
      optionValues.push(mtestOptionValues.REFERRAL);
    }

    if (
      optionsToAdd.includes(mtestOptionsActions.RESULTS)
      && this.permissionService.hasAction(mtestOptionsActions.RESULTS, actions)
      && this.permissionService.hasPermission(mtestOptionPermission.RESULTS)
    ) {
      optionValues.push(mtestOptionValues.RESULTS);
    }

    if (
      optionsToAdd.includes(mtestOptionsActions.RESULTS_VIEWER)
      && this.permissionService.hasAction(mtestOptionsActions.RESULTS_VIEWER, actions)
      && this.permissionService.hasPermission(mtestOptionPermission.RESULTS_VIEWER)
    ) {
      optionValues.push(mtestOptionValues.RESULTS_VIEWER);
    }

    if (
      optionsToAdd.includes(mtestOptionsActions.RESULTS_VIEWER_URL)
      && this.permissionService.hasAction(mtestOptionsActions.RESULTS_VIEWER_URL, actions)
      && this.permissionService.hasPermission(mtestOptionPermission.RESULTS_VIEWER_URL)
    ) {
      optionValues.push(mtestOptionValues.RESULTS_VIEWER_URL);
    }

    if (
      optionsToAdd.includes(mtestOptionsActions.IMAGE)
      && this.permissionService.hasAction(mtestOptionsActions.IMAGE, actions)
      && this.permissionService.hasPermission(mtestOptionPermission.IMAGE)
    ) {
      if (!images?.length) {
        optionValues.push(mtestOptionValues.IMAGE);
      } else {
        const imageValues = this.updateImageArray(images);
        optionValues = optionValues.concat(imageValues);
      }
    }

    if (
      optionsToAdd.includes(mtestOptionsActions.IC)
      && this.permissionService.hasAction(mtestOptionsActions.IC, actions)
      && this.permissionService.hasPermission(mtestOptionPermission.IC)
    ) {
      const informedConsentValues = this.updateInformedConsentArray(informedConsents);
      optionValues = optionValues.concat(informedConsentValues);
    }

    return optionValues;
  }

  updateImageArray(array: IdName[]): IdName[] {
    return array?.map(item => ({
      ...item,
      type: mtestOptionCases.IMAGE,
      name: `${this.translateService.instant('SI_IMAGE')}: ${item.name}`
    })) ?? [];
  }

  updateInformedConsentArray(array: IdName[]): IdName[] {
    return array?.map(item => ({
      ...item,
      type: mtestOptionCases.IC
    })) ?? [];
  }

  initOptionValuesHistory(
    optionsToAdd: string[],
    actions: string[],
    images?: IdName[]
  ): IdName[] {
    let optionValues = [];
    if (
      optionsToAdd.includes(historyOptionsActions.DOCUMENT)
      && this.permissionService.hasAction(historyOptionsActions.DOCUMENT, actions)
      && this.permissionService.hasPermission(historyOptionPermission.DOCUMENT)
    ) {
      optionValues.push(historyOptionValues.DOCUMENT);
    }

    if (
      optionsToAdd.includes(historyOptionsActions.RESULTS_VIEWER)
      && this.permissionService.hasAction(historyOptionsActions.RESULTS_VIEWER, actions)
      && this.permissionService.hasPermission(historyOptionPermission.RESULTS_VIEWER)
    ) {
      optionValues.push(historyOptionValues.RESULTS_VIEWER);
    }

    if (
      optionsToAdd.includes(historyOptionsActions.RESULTS_VIEWER_URL)
      && this.permissionService.hasAction(historyOptionsActions.RESULTS_VIEWER_URL, actions)
      && this.permissionService.hasPermission(historyOptionPermission.RESULTS_VIEWER_URL)
    ) {
      optionValues.push(historyOptionValues.RESULTS_VIEWER_URL);
    }

    if (
      optionsToAdd.includes(historyOptionsActions.IMAGE)
      && this.permissionService.hasAction(historyOptionsActions.IMAGE, actions)
      && this.permissionService.hasPermission(historyOptionPermission.IMAGE)
    ) {
      if (!images.length) {
        optionValues.push(historyOptionValues.IMAGE);
      } else {
        const imageValues = this.updateImageArray(images);
        optionValues = optionValues.concat(imageValues);
      }
    }

    return optionValues;
  }

  initAllOptionValuesMtest(actions: string[], informedConsents: IdName[]): IdName[] {
    const optionsToAdd = [
      mtestOptionsActions.REFERRAL,
      mtestOptionsActions.RESULTS,
      mtestOptionsActions.RESULTS_VIEWER,
      mtestOptionsActions.RESULTS_VIEWER_URL,
      mtestOptionsActions.IMAGE,
      mtestOptionsActions.IC
    ]
    return this.initOptionValuesMtest(optionsToAdd, actions, null, informedConsents);
  }

  initImageOptionValuesMtest(actions: string[], images: IdName[]): IdName[] {
    const optionsToAdd = [
      mtestOptionsActions.IMAGE
    ]
    return this.initOptionValuesMtest(optionsToAdd, actions, images);
  }

  initImageOptionValuesHistory(actions: string[], images: IdName[]): IdName[] {
    const optionsToAdd = [
      historyOptionsActions.IMAGE
    ]
    return this.initOptionValuesHistory(optionsToAdd, actions, images);
  }

  initReportOptionValuesMtest(actions: string[]): IdName[] {
    const optionsToAdd = [
      mtestOptionsActions.RESULTS,
      mtestOptionsActions.RESULTS_VIEWER,
      mtestOptionsActions.RESULTS_VIEWER_URL
    ]
    return this.initOptionValuesMtest(optionsToAdd, actions);
  }

  initDocumentOptionValuesHistory(actions: string[]): IdName[] {
    const optionsToAdd = [
      historyOptionsActions.DOCUMENT,
      historyOptionsActions.RESULTS_VIEWER,
      historyOptionsActions.RESULTS_VIEWER_URL
    ]
    return this.initOptionValuesHistory(optionsToAdd, actions);
  }

  initShareOptionValuesMtest(actions: string[], images: IdName[]): IdName[] {
    const optionsToAdd = [
      mtestOptionsActions.RESULTS,
      mtestOptionsActions.RESULTS_VIEWER,
      mtestOptionsActions.RESULTS_VIEWER_URL,
      mtestOptionsActions.IMAGE
    ]
    return this.initOptionValuesMtest(optionsToAdd, actions, images);
  }

  initShareOptionValuesHistory(actions: string[], images: IdName[]): IdName[] {
    const optionsToAdd = [
      historyOptionsActions.DOCUMENT,
      historyOptionsActions.RESULTS_VIEWER,
      historyOptionsActions.RESULTS_VIEWER_URL,
      historyOptionsActions.IMAGE
    ]
    return this.initOptionValuesHistory(optionsToAdd, actions, images);
  }
}
